import { useMemo } from 'react';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { SortDirection } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { useListActiveYears } from '@frontend/api-bulk-messaging';
import { useTranslation } from '@frontend/i18n';
import { MultiLocationSelector, type MultiLocationSelectorFieldProps } from '@frontend/multi-location-selector';
import { OrgLocationPicker, useOrgLocationTree } from '@frontend/org-location-picker';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { BulkEmailPrefixes, BulkMessagingPrefixes, BulkTextPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import {
  BaseFieldProps,
  Button,
  DropdownField,
  PopoverMenu,
  PopoverMenuItem,
  usePopoverMenu,
} from '@frontend/design-system';
import { useOrgLevelLocationSelector } from '../../hooks/use-org-level-location-selector';
import { toggleChangedIds } from '../../utils/toggle-changed-ids';
import { NewCampaignButton } from './new-campaign-button';

type Props = {
  accessibleLocations: string[];
  /** An undefined campaign type means both email and text campaigns should be handled, ie: the drafts page */
  campaignType?: CampaignType;
  locationsFieldProps: MultiLocationSelectorFieldProps;
  sortDropdownFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
  yearDropdownFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
};

export const CampaignListHeader = ({
  accessibleLocations,
  campaignType,
  locationsFieldProps,
  sortDropdownFieldProps,
  yearDropdownFieldProps,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { selectedOrgId: orgId, selectedLocationIds: displayedLocations } = useAppScopeStore();
  const { permission: locationPickerPermission } = useOrgLevelLocationSelector(campaignType);
  // The org picker builds its own tree from the scope catalog, so gate it on that count (not the legacy
  // accessibleLocations prop, which is sourced differently and can disagree).
  const { accessibleCount } = useOrgLocationTree({ permission: locationPickerPermission });
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const sortMenu = usePopoverMenu<HTMLButtonElement>({ placement: 'bottom-end' });

  // The picker emits the full selection, but the field's onChange toggles a SINGLE id at a time.
  const handleLocationsChange = (ids: string[]) =>
    toggleChangedIds(locationsFieldProps.locationIds.value, ids, (locationId) =>
      locationsFieldProps.locationIds.onChange?.({ name: 'locationIds', value: locationId } as never)
    );

  const { data: years = [] } = useListActiveYears({
    locationIds: locationsFieldProps.locationIds.value,
    orgId,
  });

  const trackingIdPrefix = useMemo(() => {
    if (campaignType === undefined) {
      return BulkMessagingPrefixes.Drafts;
    }
    if (campaignType === CampaignType.EMAIL) {
      return BulkEmailPrefixes.Campaigns;
    }
    return BulkTextPrefixes.Campaigns;
  }, [campaignType]);

  // Drafts (no campaignType = both channels) can't be filtered by a single channel ACL, so they keep the
  // legacy selector; only the single-channel campaign lists use the org picker (gated on its own count).
  const showOrgPicker = !!campaignType && accessibleCount > 1;
  const showLegacySelector = !campaignType && accessibleLocations.length > 1;

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: isXSmall ? 'column' : 'row',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(3),
      }}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          columnGap: theme.spacing(1),
          rowGap: theme.spacing(1),
          width: '100%',
        }}
      >
        <DropdownField
          name='year'
          label={t('View')}
          {...yearDropdownFieldProps}
          data-trackingid={`${trackingIdPrefix}-year-dropdown`}
          css={{
            flex: isXSmall ? '1 1 110px' : undefined,
            minWidth: 0,
          }}
        >
          {years.map((year, index) => (
            <DropdownField.Option key={index} value={`${year}`}>
              {year}
            </DropdownField.Option>
          ))}
        </DropdownField>

        {showLegacySelector && (
          <MultiLocationSelector
            accessibleLocations={accessibleLocations}
            css={{
              '.multi-location-selector': {
                height: theme.spacing(5),
              },
              flex: isXSmall ? '1 1 160px' : undefined,
              minWidth: 0,
            }}
            displayedLocations={displayedLocations}
            fieldProps={locationsFieldProps}
            isRequired={false}
            labelType='count'
            popoverYOffset={40}
            trackingIdPrefix={trackingIdPrefix}
            width={theme.spacing(25)}
          />
        )}

        {showOrgPicker && (
          <OrgLocationPicker
            permission={locationPickerPermission}
            value={locationsFieldProps.locationIds.value}
            onChange={handleLocationsChange}
            label={t('Locations')}
            isRequired={false}
            trackingIdPrefix={trackingIdPrefix}
            width={280}
          />
        )}

        {isXSmall ? (
          <>
            <Button
              {...sortMenu.getTriggerProps()}
              onClick={sortMenu.open}
              iconName={
                (sortDropdownFieldProps.value as SortDirection) === SortDirection.ASCENDING
                  ? 'sort-ascending'
                  : 'sort-descending'
              }
              label={t('Sort By')}
              size='medium'
              variant='secondary'
              css={{ width: 36, padding: 0 }}
              trackingId={`${trackingIdPrefix}-sort-icon-btn`}
            />
            <PopoverMenu {...sortMenu.getMenuProps()}>
              <PopoverMenuItem
                {...sortMenu.getItemProps({
                  index: 0,
                  onClick: () => sortDropdownFieldProps.onChange?.({ value: SortDirection.DESCENDING } as never),
                })}
                trackingId={`${trackingIdPrefix}-sort-newest`}
              >
                {t('Newest')}
              </PopoverMenuItem>
              <PopoverMenuItem
                {...sortMenu.getItemProps({
                  index: 1,
                  onClick: () => sortDropdownFieldProps.onChange?.({ value: SortDirection.ASCENDING } as never),
                })}
                trackingId={`${trackingIdPrefix}-sort-oldest`}
              >
                {t('Oldest')}
              </PopoverMenuItem>
            </PopoverMenu>
          </>
        ) : (
          <DropdownField
            {...sortDropdownFieldProps}
            data-trackingid={`${trackingIdPrefix}-sort-dropdown`}
            label={t('Sort By')}
            name='order'
            css={{ minWidth: theme.spacing(20) }}
          >
            <DropdownField.Option value={SortDirection.DESCENDING}>{t('Newest')}</DropdownField.Option>
            <DropdownField.Option value={SortDirection.ASCENDING}>{t('Oldest')}</DropdownField.Option>
          </DropdownField>
        )}
      </div>

      <NewCampaignButton
        campaignType={campaignType}
        selectedLocationIds={locationsFieldProps.locationIds.value}
        trackingIdPrefix={trackingIdPrefix}
      />
    </div>
  );
};
